import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Target, Eye } from "lucide-react";
import { Seo, Reveal, ParallaxImage } from "../components/Seo";
import { PageHero, SectionHead, Eyebrow, CtaBand } from "../components/blocks";
import { IMG } from "../lib/site";

const VALUES = [
  {
    title: "Engineering discipline",
    desc: "We design before we build, test what we ship, and document what we operate.",
  },
  {
    title: "Honesty about readiness",
    desc: "Products and capabilities are labelled by their true status — never presented as more mature than they are.",
  },
  {
    title: "Client outcomes first",
    desc: "Recommendations serve the client's operating reality and constraints, not a technology preference.",
  },
  {
    title: "Long-term ownership",
    desc: "We build systems our clients' teams can run, extend and audit long after handover.",
  },
];

const CAPABILITIES = [
  "Solution and enterprise architecture",
  "Cloud and infrastructure engineering",
  "Security architecture and identity",
  "Product and platform engineering",
  "Systems integration and interoperability",
  "Data platforms and workflow digitisation",
];

export default function About() {
  return (
    <>
      <Seo
        title="About Us — Softline Nigeria"
        description="Softline Technologies and Services Nigeria Limited: a Nigerian digital products and enterprise technology services company, part of the Softline group of Sweden."
        path="/about"
      />
      <PageHero
        eyebrow="About Softline Nigeria"
        title={["Technology expertise.", "Nigerian accountability.", "Group standards."]}
        lead="Softline Technologies and Services Nigeria Limited is the Nigerian subsidiary of Softline Technologies and Services AB, Sweden — combining international engineering discipline with direct accountability to the Nigerian market."
        crumbs={[{ label: "Home", to: "/" }, { label: "About Us" }]}
      />

      <section className="bg-white" data-testid="about-story">
        <div className="shell py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <SectionHead
              eyebrow="Company overview"
              title="A hybrid model: products we own, services we deliver."
              lead="Softline Nigeria operates two complementary lines of business. First, proprietary digital products — SoftEbox, myID and CoFundApp — designed and engineered in-house. Second, enterprise technology services: architecture, cloud, security, engineering, integration and data engagements for Nigerian organisations. The model matters: product ownership keeps our engineering honest, and consulting keeps our products grounded in real operational needs."
            />
            <Reveal delay={0.2}>
              <div className="mt-10 grid sm:grid-cols-2 gap-6">
                <div className="border border-charcoal/10 p-6">
                  <Target size={22} className="text-charcoal" aria-hidden="true" />
                  <h3 className="font-display font-bold text-lg mt-4">Mission</h3>
                  <p className="text-sm leading-relaxed text-charcoal/70 mt-2">
                    To give Nigerian enterprises, government institutions and financial organisations access to digital
                    products and engineering services that are secure, practical and built to last.
                  </p>
                </div>
                <div className="border border-charcoal/10 p-6">
                  <Eye size={22} className="text-charcoal" aria-hidden="true" />
                  <h3 className="font-display font-bold text-lg mt-4">Vision</h3>
                  <p className="text-sm leading-relaxed text-charcoal/70 mt-2">
                    A Nigerian digital economy where organisations trust the systems they run — because those systems were
                    engineered with discipline and transparency.
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <h3 className="font-display font-bold text-xl mt-12">Values</h3>
              <dl className="mt-6 grid sm:grid-cols-2 gap-px bg-charcoal/10 border border-charcoal/10">
                {VALUES.map((v) => (
                  <div key={v.title} className="bg-white p-6">
                    <dt className="font-semibold text-charcoal">{v.title}</dt>
                    <dd className="text-sm text-charcoal/65 leading-relaxed mt-2">{v.desc}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
          <div className="lg:col-span-5 space-y-6">
            <Reveal delay={0.15}>
              <figure className="border border-charcoal/10 bg-surface p-2">
                <ParallaxImage
                  src={IMG.team}
                  alt="Professionals collaborating in a modern office — illustrative photography"
                  className="aspect-[4/3] w-full"
                  distance={28}
                />
                <figcaption className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/60">
                  Illustrative photography — not Softline personnel
                </figcaption>
              </figure>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="bg-charcoal text-white p-8" data-testid="about-capabilities">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-lime">Core capabilities</p>
                <ul className="mt-6 space-y-3.5">
                  {CAPABILITIES.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-[15px] text-softgrey">
                      <CheckCircle2 size={18} className="text-lime mt-0.5 shrink-0" aria-hidden="true" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-surface" data-testid="about-leadership">
        <div className="shell py-20">
          <Reveal>
            <div className="border border-charcoal/15 bg-white p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
              <div>
                <Eyebrow>Leadership</Eyebrow>
                <p className="mt-4 max-w-2xl text-charcoal/75 leading-relaxed">
                  Executive profiles and organisational details will be published here once approved by the content owners.
                  <span className="block mt-2 font-mono text-xs uppercase tracking-[0.15em] text-charcoal/50">
                    [Placeholder — leadership information pending verification]
                  </span>
                </p>
              </div>
              <Link
                to="/about/our-group"
                data-testid="about-group-link"
                className="inline-flex items-center gap-2 font-semibold text-charcoal border border-charcoal/25 px-6 py-3 hover:bg-charcoal hover:text-white transition-colors duration-200 shrink-0"
              >
                Meet the group <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
